import ChevronRightThinIcon from '@assets/icons/chevron-right-thin.svg';
import React from 'react';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { Analytics } from '@/utils/analytics';
import { createStyles } from '@/contexts/theme-context';
import { CurrentOrdersHubCardDataItem, OperatorEmployeeContact } from '@/types';
import { fennelTrack } from '@/analytics/fennel/hook';
import { formatCurrency } from '@/utils/money';
import { getStableId, HubOrder } from '@/hooks/insights-hub/order-option';
import { getTranslationValue } from '@/utils/translations';
import { HubCardBase } from '@/components/insights-hub/hub-card-base';
import { HubCurrentOrdersSkeleton } from '@/components/insights-hub/hub-current-orders-skeleton';
import { INSIGHTS_HUB_ROUTES } from '@/navigators/routes';
import {
  InsightsHubStackParams,
  InvoiceDetails,
  OrderDetails,
} from '@/navigators/types';
import { OrderAction } from '@/components/insights-hub/order-action';
import { Platform, TouchableOpacity } from 'react-native';
import { RestaurantInfoHeader } from '@/components/insights-hub/restaurant-info-header';
import { StackNavigationProp } from '@react-navigation/stack';
import { useHubCardNavigation } from '@/hooks/insights-hub/use-hub-card-navigation';
import { useHubCardOrders } from '@/hooks/insights-hub/use-current-orders';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useSupplierCurrencyCode } from '@/hooks/use-supplier-currency-code';

type NavigationType = StackNavigationProp<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.DraftDetails
>;

const NUM_ORDERS_DISPLAYED = 3;

interface Props {
  openPhonePickerBottomSheet: (
    operatorEmployees: OperatorEmployeeContact[],
  ) => void;
}

export const HubCurrentOrders: React.FC<Props> = ({
  openPhonePickerBottomSheet,
}) => {
  const [styles, theme] = useStyles();

  const { navigate } = useNavigation<NavigationType>();
  const { navigateToOrderManagement } = useHubCardNavigation();
  const { isSmallLayout } = useLayoutContext();

  const { data, error, loading } = useHubCardOrders();
  // handle empty orders
  const supplierUUID =
    data && data.card_data.length > 0
      ? data.card_data[0].supplier_uuid
      : undefined;
  const { currencyCode: supplierCurrencyCode } =
    useSupplierCurrencyCode(supplierUUID);

  // cache policy is cache-and-network,
  //   loading will be true while cache data is displayed and network data is being retrieved
  if (loading && !data && isSmallLayout && Platform.OS !== 'web') {
    return <HubCurrentOrdersSkeleton />;
  }

  if (!data || error || data.card_data.length === 0) {
    return null;
  }

  const onNavigateToOrderManagement = () => {
    if (Platform.OS !== 'web') {
      // TODO(DSR-2973): navigation to order management disabled on web for now
      navigateToOrderManagement();
    }
    Analytics.track('insights_hub.hub_current_orders_section.press', {
      platform: Platform.OS,
    });
  };

  const onNavigateToDraftDetails = (orderDetails: OrderDetails) => {
    navigate(INSIGHTS_HUB_ROUTES.DraftDetails, orderDetails);
    fennelTrack(AnalyticEvent.ORDER_PRESS, {
      ...orderDetails,
      isDraft: true,
      origin: INSIGHTS_HUB_ROUTES.Base,
    });
  };

  const onNavigateToPlacedOrderDetails = (orderDetails: OrderDetails) => {
    navigate(INSIGHTS_HUB_ROUTES.OrderDetails, orderDetails);
    fennelTrack(AnalyticEvent.ORDER_PRESS, {
      ...orderDetails,
      isDraft: false,
      origin: INSIGHTS_HUB_ROUTES.Base,
    });
  };

  const onNavigateToInvoiceDetails = (invoiceDetails: InvoiceDetails) => {
    navigate(INSIGHTS_HUB_ROUTES.InvoiceDetails, invoiceDetails);
    fennelTrack(AnalyticEvent.INVOICE_PRESS, {
      ...invoiceDetails,
      origin: INSIGHTS_HUB_ROUTES.Base,
    });
  };

  const onNavigateToOrderDetails = (order: CurrentOrdersHubCardDataItem) => {
    if (Platform.OS === 'web') {
      // TODO(DSR-2973): navigation to order management disabled on web for now
      return;
    }

    // Invoice-only data can be included, which won't have a navigable order uuid
    if (!order.order_uuid) {
      return;
    }

    const orderDetails: OrderDetails = {
      contacts: order.operator_employees || [],
      orderUUID: order.order_uuid,
      restaurantUUID: order.restaurant_uuid,
      supplierUUID: order.supplier_uuid,
    };

    if (order.is_draft_order) {
      onNavigateToDraftDetails(orderDetails);
    } else {
      onNavigateToPlacedOrderDetails(orderDetails);
    }
  };

  const onPressOrder = (order: CurrentOrdersHubCardDataItem) => {
    // navigate to invoice details if available
    if (order.invoice_uuid) {
      onNavigateToInvoiceDetails({
        invoiceUUID: order.invoice_uuid,
        restaurantUUID: order.restaurant_uuid,
        supplierUUID: order.supplier_uuid,
      });
      return;
    }

    // if the order has an order_uuid, navigate to the order details, order or draft details
    if ('order_uuid' in order) {
      onNavigateToOrderDetails(order);
    }
  };

  return (
    <HubCardBase
      actionsElement={
        <TouchableOpacity
          accessibilityLabel={getTranslationValue(data.title)}
          onPress={onNavigateToOrderManagement}
          style={styles.actionsElement}
        >
          <ChevronRightThinIcon stroke={theme.sk.color.icon.primary} />
        </TouchableOpacity>
      }
      childrenStyle={styles.hubCardContentContainer}
      description={getTranslationValue(data.subtitle)}
      title={getTranslationValue(data.title)}
    >
      {data.card_data.slice(0, NUM_ORDERS_DISPLAYED).map((order) => (
        <RestaurantInfoHeader
          key={getStableId(order as unknown as HubOrder)}
          onPress={() => onPressOrder(order)}
          restaurantUUID={order.restaurant_uuid}
          rightElement={
            <OrderAction
              contacts={
                order.is_draft_order
                  ? order.operator_employees || []
                  : undefined
              }
              itemCountDisplay={
                order.item_count_display
                  ? getTranslationValue(order.item_count_display)
                  : undefined
              }
              onPress={() => {
                if (
                  order.operator_employees &&
                  order.operator_employees.length > 0
                ) {
                  openPhonePickerBottomSheet(order.operator_employees);
                  fennelTrack(AnalyticEvent.PHONE_PICKER_SHEET_OPEN, {
                    contacts: order.operator_employees,
                    orderUUID: order.order_uuid!,
                    origin: INSIGHTS_HUB_ROUTES.Base,
                    platform: Platform.OS,
                    restaurantName: order.restaurant_name,
                    restaurantUUID: order.restaurant_uuid,
                  });
                }
              }}
              orderAmount={
                order.order_amount_in_micros
                  ? formatCurrency(
                      order.order_amount_in_micros,
                      supplierCurrencyCode,
                    )
                  : undefined
              }
            />
          }
          subtitle={order.description}
          title={order.restaurant_name_i18n}
        />
      ))}
    </HubCardBase>
  );
};

const useStyles = createStyles((theme) => ({
  actionsElement: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderRadius: 16,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  hubCardContentContainer: {
    gap: 16,
    paddingTop: 16,
  },
}));
